import type {
  Ballot,
  BallotSelection,
  PollResult,
  PollResultTeam,
  PollStatus,
} from './types.js';

/* ------------------------------------------------------------------ */
/* Ballot point system                                                 */
/* ------------------------------------------------------------------ */

/** Every official FanVote25 ballot ranks exactly this many teams. */
export const BALLOT_SIZE = 25;

/** Points awarded to the last ranked slot (#25). */
export const POINTS_AT_LAST_RANK = 10;

/** Each position up the ballot is worth this much more. */
export const POINTS_PER_POSITION = 5;

/**
 * Canonical FanVote25 scoring function.
 *
 *   #25 = 10 pts, and every position above it adds exactly 5:
 *   #24 = 15 ... #10 = 85 ... #2 = 125, #1 = 130.
 *
 * This is the single source of truth. Points are always derived from rank,
 * never stored as an independent editable value.
 */
export function pointsForRank(rank: number): number {
  if (!Number.isInteger(rank) || rank < 1 || rank > BALLOT_SIZE) return 0;
  return POINTS_AT_LAST_RANK + (BALLOT_SIZE - rank) * POINTS_PER_POSITION;
}

/** Maximum points a single ballot can award to one team (#1). */
export const MAX_BALLOT_POINTS = pointsForRank(1);

/** Full rank -> points table, handy for legends and tooltips. */
export const POINTS_TABLE: { rank: number; points: number }[] = Array.from(
  { length: BALLOT_SIZE },
  (_, index) => ({ rank: index + 1, points: pointsForRank(index + 1) }),
);

/** Total points a complete ballot distributes: 25 slots, 10..130 => 1,750. */
export const POINTS_PER_COMPLETE_BALLOT = POINTS_TABLE.reduce((sum, row) => sum + row.points, 0);

/** Re-derives the points on every selection and sorts by rank. */
export function normalizeSelections(selections: BallotSelection[]): BallotSelection[] {
  return [...selections]
    .filter((selection) => selection.rank >= 1 && selection.rank <= BALLOT_SIZE)
    .sort((a, b) => a.rank - b.rank)
    .map((selection) => ({
      teamId: selection.teamId,
      rank: selection.rank,
      points: pointsForRank(selection.rank),
    }));
}

export interface BallotValidation {
  valid: boolean;
  errors: string[];
  rankedCount: number;
  missingRanks: number[];
  duplicateTeamIds: string[];
}

/** Guards the submit path: 25 unique teams, one per slot, no gaps. */
export function validateBallot(selections: BallotSelection[]): BallotValidation {
  const errors: string[] = [];
  const seenTeams = new Map<string, number>();
  const seenRanks = new Map<number, number>();
  const duplicateTeamIds: string[] = [];

  for (const selection of selections) {
    seenTeams.set(selection.teamId, (seenTeams.get(selection.teamId) ?? 0) + 1);
    seenRanks.set(selection.rank, (seenRanks.get(selection.rank) ?? 0) + 1);
  }

  for (const [teamId, count] of seenTeams) {
    if (count > 1) duplicateTeamIds.push(teamId);
  }
  if (duplicateTeamIds.length > 0) {
    errors.push('A team can only appear once on a ballot.');
  }

  for (const [rank, count] of seenRanks) {
    if (count > 1) errors.push(`Rank #${rank} has more than one team.`);
  }

  const missingRanks: number[] = [];
  for (let rank = 1; rank <= BALLOT_SIZE; rank += 1) {
    if (!seenRanks.has(rank)) missingRanks.push(rank);
  }
  if (missingRanks.length > 0) {
    errors.push(`${missingRanks.length} of ${BALLOT_SIZE} positions still need a team.`);
  }

  return {
    valid: errors.length === 0,
    errors,
    rankedCount: selections.length,
    missingRanks,
    duplicateTeamIds,
  };
}

/* ------------------------------------------------------------------ */
/* Poll calculation                                                    */
/* ------------------------------------------------------------------ */

/** Number of teams that make the official poll. */
export const POLL_SIZE = 25;

interface Accumulator {
  teamId: string;
  totalPoints: number;
  firstPlaceVotes: number;
  ballotAppearances: number;
  rankSum: number;
  highestBallotRank: number;
  lowestBallotRank: number;
  distribution: Record<number, number>;
  top5Appearances: number;
  top10Appearances: number;
}

export interface PollCalculationInput {
  seasonId: string;
  weekId: string;
  weekNumber: number;
  /** Only submitted, non-excluded ballots should be passed in. */
  ballots: Pick<Ballot, 'id' | 'selections'>[];
  /** Released poll from the prior week, used for movement and tiebreaker #6. */
  previousPoll?: Pick<PollResult, 'teams'> | null;
  status?: PollStatus;
  calculatedAt?: string;
}

/**
 * Deterministic FanVote25 poll calculation.
 *
 * Teams are sorted by total ballot points descending, then by the documented
 * tiebreaker ladder. Everything that received at least one point is returned,
 * so the caller can slice ranks 1-25 as the official poll and the remainder as
 * "Also Receiving Votes".
 */
export function calculatePoll(input: PollCalculationInput): PollResult {
  const { seasonId, weekId, weekNumber, ballots, previousPoll } = input;
  const accumulators = new Map<string, Accumulator>();

  const previousRanks = new Map<string, number>();
  for (const team of previousPoll?.teams ?? []) {
    if (team.rank <= POLL_SIZE) previousRanks.set(team.teamId, team.rank);
  }

  let totalPointsCast = 0;

  for (const ballot of ballots) {
    for (const selection of normalizeSelections(ballot.selections)) {
      const points = pointsForRank(selection.rank);
      if (points === 0) continue;
      totalPointsCast += points;

      let accumulator = accumulators.get(selection.teamId);
      if (!accumulator) {
        accumulator = {
          teamId: selection.teamId,
          totalPoints: 0,
          firstPlaceVotes: 0,
          ballotAppearances: 0,
          rankSum: 0,
          highestBallotRank: BALLOT_SIZE,
          lowestBallotRank: 1,
          distribution: {},
          top5Appearances: 0,
          top10Appearances: 0,
        };
        accumulators.set(selection.teamId, accumulator);
      }

      accumulator.totalPoints += points;
      accumulator.ballotAppearances += 1;
      accumulator.rankSum += selection.rank;
      accumulator.distribution[selection.rank] = (accumulator.distribution[selection.rank] ?? 0) + 1;
      if (selection.rank === 1) accumulator.firstPlaceVotes += 1;
      if (selection.rank <= 5) accumulator.top5Appearances += 1;
      if (selection.rank <= 10) accumulator.top10Appearances += 1;
      accumulator.highestBallotRank = Math.min(accumulator.highestBallotRank, selection.rank);
      accumulator.lowestBallotRank = Math.max(accumulator.lowestBallotRank, selection.rank);
    }
  }

  const rows = [...accumulators.values()].map((accumulator) => ({
    accumulator,
    averageRank: accumulator.rankSum / Math.max(1, accumulator.ballotAppearances),
    previousRank: previousRanks.get(accumulator.teamId) ?? null,
  }));

  rows.sort((a, b) => comparePollRows(a, b));

  const teams: PollResultTeam[] = rows.map((row, index) => {
    const rank = index + 1;
    const previousRank = row.previousRank;
    const isRanked = rank <= POLL_SIZE;
    const wasRanked = previousRank !== null;
    return {
      rank,
      teamId: row.accumulator.teamId,
      totalPoints: row.accumulator.totalPoints,
      firstPlaceVotes: row.accumulator.firstPlaceVotes,
      ballotAppearances: row.accumulator.ballotAppearances,
      averageRank: Number(row.averageRank.toFixed(2)),
      highestBallotRank: row.accumulator.highestBallotRank,
      lowestBallotRank: row.accumulator.lowestBallotRank,
      previousRank,
      movement: wasRanked && isRanked ? previousRank - rank : null,
      isNew: isRanked && !wasRanked,
      distribution: row.accumulator.distribution,
      top5Appearances: row.accumulator.top5Appearances,
      top10Appearances: row.accumulator.top10Appearances,
    };
  });

  const nowRanked = new Set(teams.filter((team) => team.rank <= POLL_SIZE).map((team) => team.teamId));
  const droppedOut = [...previousRanks.entries()]
    .filter(([teamId]) => !nowRanked.has(teamId))
    .map(([teamId, previousRank]) => ({ teamId, previousRank }))
    .sort((a, b) => a.previousRank - b.previousRank);

  return {
    id: `${seasonId}-w${weekNumber}`,
    seasonId,
    weekId,
    weekNumber,
    status: input.status ?? 'preview',
    calculatedAt: input.calculatedAt ?? new Date().toISOString(),
    ballotsCounted: ballots.length,
    totalPointsCast,
    teams,
    droppedOut,
  };
}

interface SortableRow {
  accumulator: Accumulator;
  averageRank: number;
  previousRank: number | null;
}

/**
 * FanVote25 tiebreaker ladder - applied in order, fully deterministic:
 *
 *   0. Most total ballot points
 *   1. Most first-place votes
 *   2. Most Top 5 appearances
 *   3. Most Top 10 appearances
 *   4. Higher average ballot position (lower number wins)
 *   5. More ballot appearances
 *   6. Higher ranking in last week's released poll (unranked loses)
 *   7. Team id, ascending - the final deterministic backstop
 *
 * Exported so the admin console and the docs can render the same ladder.
 */
export function comparePollRows(a: SortableRow, b: SortableRow): number {
  if (b.accumulator.totalPoints !== a.accumulator.totalPoints) {
    return b.accumulator.totalPoints - a.accumulator.totalPoints;
  }
  if (b.accumulator.firstPlaceVotes !== a.accumulator.firstPlaceVotes) {
    return b.accumulator.firstPlaceVotes - a.accumulator.firstPlaceVotes;
  }
  if (b.accumulator.top5Appearances !== a.accumulator.top5Appearances) {
    return b.accumulator.top5Appearances - a.accumulator.top5Appearances;
  }
  if (b.accumulator.top10Appearances !== a.accumulator.top10Appearances) {
    return b.accumulator.top10Appearances - a.accumulator.top10Appearances;
  }
  if (a.averageRank !== b.averageRank) {
    return a.averageRank - b.averageRank;
  }
  if (b.accumulator.ballotAppearances !== a.accumulator.ballotAppearances) {
    return b.accumulator.ballotAppearances - a.accumulator.ballotAppearances;
  }
  const aPrevious = a.previousRank ?? Number.MAX_SAFE_INTEGER;
  const bPrevious = b.previousRank ?? Number.MAX_SAFE_INTEGER;
  if (aPrevious !== bPrevious) {
    return aPrevious - bPrevious;
  }
  return a.accumulator.teamId.localeCompare(b.accumulator.teamId);
}

export const TIEBREAKER_LADDER = [
  'Most total ballot points',
  'Most first-place votes',
  'Most Top 5 appearances',
  'Most Top 10 appearances',
  'Higher average ballot position',
  'More ballot appearances',
  "Higher ranking in last week's released poll",
  'Team id, ascending (deterministic backstop)',
] as const;

/** The official Top 25 slice of a calculated poll. */
export function officialTop25(poll: Pick<PollResult, 'teams'>): PollResultTeam[] {
  return poll.teams.filter((team) => team.rank <= POLL_SIZE);
}

/** Everything below #25 that still received at least one ballot point. */
export function alsoReceivingVotes(poll: Pick<PollResult, 'teams'>): PollResultTeam[] {
  return poll.teams.filter((team) => team.rank > POLL_SIZE);
}
